/**
 * Fill-cavities proposal card (the runtime HITL gate). The fill edits the
 * structure IN PLACE, like the toolbar's Fill cavities button. Pure
 * presentational: it shows the RESOLVED target (name + number + current
 * volume), the previewed volume added by filling, and a note that the
 * structure itself changes (undo restores it); nothing mutates until the
 * clinician presses Apply. The card frame, status chip, hover preview, and
 * Apply/Reject gate live in {@link ProposalCardShell}.
 */
import { Typography } from '@mui/material';
import { formatSignedCm3 } from '~/utils/units';
import ProposalCardShell, {
    PreviewedVolumeLine,
    ProposalSummaryBox,
    TargetLine,
    type ProposalStatus,
} from './ProposalCardShell';

export interface FillCavitiesProposalCardProps {
    readonly targetName: string;
    readonly targetRoiNumber: number;
    readonly currentVolumeCm3: number;
    readonly volumeDeltaCm3: number;
    readonly status: ProposalStatus;
    readonly failureReason?: string;
    readonly onApply: () => void;
    readonly onReject: () => void;
    /** Hover/focus the card to preview the proposed geometry in the viewport. */
    readonly onPreviewEnter: () => void;
    readonly onPreviewLeave: () => void;
}

export default function FillCavitiesProposalCard({
    targetName,
    targetRoiNumber,
    currentVolumeCm3,
    volumeDeltaCm3,
    status,
    failureReason,
    onApply,
    onReject,
    onPreviewEnter,
    onPreviewLeave,
}: FillCavitiesProposalCardProps) {
    return (
        <ProposalCardShell
            title="Fill cavities"
            ariaLabel="Fill cavities proposal"
            status={status}
            failureReason={failureReason}
            onApply={onApply}
            onReject={onReject}
            onPreviewEnter={onPreviewEnter}
            onPreviewLeave={onPreviewLeave}
        >
            <TargetLine
                targetName={targetName}
                targetRoiNumber={targetRoiNumber}
                currentVolumeCm3={currentVolumeCm3}
            />

            <ProposalSummaryBox>
                fill enclosed holes (3D) in “{targetName}”
            </ProposalSummaryBox>

            <PreviewedVolumeLine
                label="Previewed volume change"
                value={formatSignedCm3(volumeDeltaCm3)}
            />

            <Typography variant="caption" color="text.secondary">
                This edits “{targetName}” itself, like the toolbar&apos;s Fill
                cavities button; no new structure is created. Undo restores it.
            </Typography>
        </ProposalCardShell>
    );
}
